/**
 * Lucy Quota & Monetization Module v2.0 (Token System)
 * Unified token-based system: 3 free daily tokens per browser.
 * Image=1 token, 3D=2 tokens, Video=3 tokens.
 * Bundle, Search, Discovery = always free.
 *
 * Public API: window.LucyQuota
 * Dependencies: window.LucyAnalytics (optional)
 */
(function () {
  'use strict';
  if (window._lucyQuotaInit) return;
  window._lucyQuotaInit = true;

  var API_BASE = 'https://ai-api.wowstore.live/api/lucy';
  var BROWSER_ID_KEY = 'lucy_browser_id';
  var QUOTA_CACHE_KEY = 'lucy_quota_cache';
  var CACHE_TTL_MS = 60000; // 1 minute

  // Token costs
  var TOKEN_COSTS = { image_gen: 1, '3d_gen': 2, video_gen: 3, voice_ext: 1 };
  var FREE_DAILY_TOKENS = 3;

  // Product URLs (Shopify handles)
  var PRODUCTS = {
    topup: { title: 'Lucy Style Top-Up', price: '\u20AC4.99', handle: 'lucy-style-top-up',
      desc: '20 tokens \u2014 ~20 images, 10 3D models, or 6 videos' },
    capsule: { title: 'Lucy Capsule Guide', price: '\u20AC9.99', handle: 'lucy-capsule-guide',
      desc: '50 tokens \u2014 ~50 images, 25 3D models, or 16 videos' },
    vip: { title: 'Lucy VIP Day Pass', price: '\u20AC2.99', handle: 'lucy-vip-day-pass',
      desc: 'Unlimited everything for 24 hours' }
  };

  // === Browser ID ===
  function getBrowserId() {
    var id = localStorage.getItem(BROWSER_ID_KEY);
    if (!id) {
      id = 'lb_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
      localStorage.setItem(BROWSER_ID_KEY, id);
    }
    return id;
  }

  // === Quota Cache ===
  function getCachedQuota() {
    try {
      var raw = sessionStorage.getItem(QUOTA_CACHE_KEY);
      if (!raw) return null;
      var cache = JSON.parse(raw);
      if (Date.now() - cache._ts > CACHE_TTL_MS) return null;
      return cache;
    } catch (e) { return null; }
  }

  function setCachedQuota(quota) {
    try {
      quota._ts = Date.now();
      sessionStorage.setItem(QUOTA_CACHE_KEY, JSON.stringify(quota));
    } catch (e) { /* ignore */ }
  }

  // === Fetch Quota from API ===
  function fetchQuota(callback) {
    var bid = getBrowserId();
    fetch(API_BASE + '/quota?browser_id=' + encodeURIComponent(bid))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        setCachedQuota(data);
        if (callback) callback(data);
      })
      .catch(function () {
        if (callback) callback(null);
      });
  }

  // === Get Token Balance (from cache or API) ===
  function getTokenBalance(callback) {
    var cached = getCachedQuota();
    if (cached && cached.tokens) {
      var bal = {
        total: cached.tokens.total_remaining || 0,
        free: cached.tokens.free_remaining || 0,
        premium: cached.tokens.premium_remaining || 0,
        is_vip: cached.is_vip || false,
        costs: cached.tokens.costs || TOKEN_COSTS
      };
      if (callback) callback(bal);
      return bal;
    }
    // No cache — fetch
    if (callback) {
      fetchQuota(function (data) {
        if (data && data.tokens) {
          callback({
            total: data.tokens.total_remaining || 0,
            free: data.tokens.free_remaining || 0,
            premium: data.tokens.premium_remaining || 0,
            is_vip: data.is_vip || false,
            costs: data.tokens.costs || TOKEN_COSTS
          });
        } else {
          callback({ total: FREE_DAILY_TOKENS, free: FREE_DAILY_TOKENS, premium: 0, is_vip: false, costs: TOKEN_COSTS });
        }
      });
    }
    return { total: FREE_DAILY_TOKENS, free: FREE_DAILY_TOKENS, premium: 0, is_vip: false, costs: TOKEN_COSTS };
  }

  // === Check if user can afford a feature ===
  function canAfford(feature, callback) {
    var cost = TOKEN_COSTS[feature] || 1;
    getTokenBalance(function (bal) {
      if (bal.is_vip) {
        if (callback) callback({ allowed: true, remaining: 999, cost: cost, is_vip: true });
        return;
      }
      var allowed = bal.total >= cost;
      if (callback) callback({ allowed: allowed, remaining: bal.total, cost: cost, is_vip: false });
    });
  }

  // === Legacy checkQuota (backward compat) ===
  function checkQuota(type, callback) {
    // Bundle and deep_turns are now always free
    if (type === 'bundles' || type === 'deep_turns') {
      if (callback) callback({ allowed: true, remaining: 999, cached: true });
      return;
    }
    // Map old types to token features
    var featureMap = { tryon: 'image_gen', video: 'video_gen', '3d': '3d_gen', voice: 'voice_ext' };
    var feature = featureMap[type] || type;
    if (TOKEN_COSTS[feature] !== undefined) {
      canAfford(feature, function (result) {
        if (callback) callback({ allowed: result.allowed, remaining: result.remaining, cached: false });
      });
      return;
    }
    // Voice seconds — check old-style
    var cached = getCachedQuota();
    if (cached && cached.remaining) {
      var remaining = cached.remaining[type] || 0;
      if (callback) callback({ allowed: remaining > 0, remaining: remaining, cached: true });
      return;
    }
    if (callback) callback({ allowed: true, remaining: 99, cached: false });
  }

  // === Update Cache from Brain Response ===
  function updateCache(quota) {
    if (quota) setCachedQuota(quota);
  }

  // === Invalidate cache (after token consumption) ===
  function invalidateCache() {
    sessionStorage.removeItem(QUOTA_CACHE_KEY);
  }

  // === Get Remaining (sync, from cache) ===
  function getRemaining() {
    var cached = getCachedQuota();
    if (cached && cached.remaining) return cached.remaining;
    return { deep_turns: 999, bundles: 999, voice_seconds: 120, tryons: 3 };
  }

  // === Build token cost info DOM element ===
  function buildCostInfo() {
    var costInfo = document.createElement('div');
    costInfo.style.cssText = 'background:#f8f9fa;border-radius:10px;padding:12px 14px;margin-bottom:16px;font-size:13px;color:#555;';

    var header = document.createElement('div');
    header.style.cssText = 'font-weight:600;margin-bottom:6px;color:#333;';
    header.textContent = 'Token Costs';
    costInfo.appendChild(header);

    var items = [
      { label: '\uD83D\uDDBC\uFE0F Image Try-On', cost: '1 \uD83E\uDE99' },
      { label: '\uD83D\uDDAB\uFE0F 3D Model', cost: '2 \uD83E\uDE99' },
      { label: '\uD83C\uDFAC Video', cost: '3 \uD83E\uDE99' }
    ];
    items.forEach(function (item) {
      var row = document.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;margin-bottom:3px;';
      var lbl = document.createElement('span');
      lbl.textContent = item.label;
      var val = document.createElement('span');
      val.style.fontWeight = '600';
      val.textContent = item.cost;
      row.appendChild(lbl);
      row.appendChild(val);
      costInfo.appendChild(row);
    });

    return costInfo;
  }

  // === Paywall Modal ===
  function showPaywall(reason, featureCost) {
    // Remove existing modal if any
    var existing = document.getElementById('lucy-paywall-modal');
    if (existing) existing.remove();

    trackEvent('paywall_shown', { reason: reason || 'insufficient_tokens' });

    var overlay = document.createElement('div');
    overlay.id = 'lucy-paywall-modal';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:100001;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;';

    var modal = document.createElement('div');
    modal.style.cssText = 'background:#fff;border-radius:16px;max-width:420px;width:100%;padding:28px 24px;position:relative;max-height:90vh;overflow-y:auto;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;';

    // Close button
    var close = document.createElement('button');
    close.textContent = '\u2715';
    close.style.cssText = 'position:absolute;top:12px;right:14px;background:none;border:none;font-size:20px;cursor:pointer;color:#666;padding:4px;';
    close.onclick = function () { overlay.remove(); };
    modal.appendChild(close);

    // Header
    var h2 = document.createElement('h2');
    h2.textContent = 'Get More Tokens \u2728';
    h2.style.cssText = 'margin:0 0 6px;font-size:22px;color:#1a1a2e;';
    modal.appendChild(h2);

    var sub = document.createElement('p');
    sub.style.cssText = 'margin:0 0 8px;color:#666;font-size:14px;';
    if (featureCost) {
      sub.textContent = 'This feature costs ' + featureCost + ' token' + (featureCost > 1 ? 's' : '') + '. Get more tokens to continue.';
    } else {
      sub.textContent = 'You\'ve used your 3 free daily tokens. Get more to keep creating.';
    }
    modal.appendChild(sub);

    // Token cost info
    modal.appendChild(buildCostInfo());

    // Product cards
    var products = [
      { key: 'vip', badge: 'BEST VALUE', color: '#ec4899' },
      { key: 'topup', badge: 'POPULAR', color: '#8b5cf6' },
      { key: 'capsule', badge: 'COMPLETE', color: '#06b6d4' }
    ];

    products.forEach(function (p) {
      var prod = PRODUCTS[p.key];
      var card = document.createElement('a');
      card.href = '/products/' + prod.handle;
      card.style.cssText = 'display:block;border:2px solid #f0f0f0;border-radius:12px;padding:16px;margin-bottom:12px;text-decoration:none;color:inherit;transition:border-color 0.2s;';
      card.onmouseenter = function () { card.style.borderColor = p.color; };
      card.onmouseleave = function () { card.style.borderColor = '#f0f0f0'; };
      card.onclick = function () {
        trackEvent('upgrade_clicked', { product: p.key, price: prod.price });
      };

      var top = document.createElement('div');
      top.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;';

      var badge = document.createElement('span');
      badge.textContent = p.badge;
      badge.style.cssText = 'font-size:10px;font-weight:700;color:#fff;background:' + p.color + ';padding:2px 8px;border-radius:10px;letter-spacing:0.5px;';
      top.appendChild(badge);

      var price = document.createElement('span');
      price.textContent = prod.price;
      price.style.cssText = 'font-size:18px;font-weight:700;color:' + p.color + ';';
      top.appendChild(price);

      card.appendChild(top);

      var title = document.createElement('div');
      title.textContent = prod.title;
      title.style.cssText = 'font-size:15px;font-weight:600;margin-bottom:4px;';
      card.appendChild(title);

      var desc = document.createElement('div');
      desc.textContent = prod.desc;
      desc.style.cssText = 'font-size:12px;color:#888;';
      card.appendChild(desc);

      modal.appendChild(card);
    });

    // Already purchased link
    var activateLink = document.createElement('button');
    activateLink.textContent = 'Already purchased? Activate with your email \u2192';
    activateLink.style.cssText = 'background:none;border:none;color:#ec4899;font-size:13px;cursor:pointer;padding:8px 0;width:100%;text-align:center;';
    activateLink.onclick = function () {
      overlay.remove();
      showActivation();
    };
    modal.appendChild(activateLink);

    overlay.appendChild(modal);
    overlay.onclick = function (e) {
      if (e.target === overlay) overlay.remove();
    };
    document.body.appendChild(overlay);
  }

  // === Activation Form ===
  function showActivation() {
    var existing = document.getElementById('lucy-activate-modal');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.id = 'lucy-activate-modal';
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:100001;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;';

    var modal = document.createElement('div');
    modal.style.cssText = 'background:#fff;border-radius:16px;max-width:380px;width:100%;padding:28px 24px;position:relative;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;';

    var close = document.createElement('button');
    close.textContent = '\u2715';
    close.style.cssText = 'position:absolute;top:12px;right:14px;background:none;border:none;font-size:20px;cursor:pointer;color:#666;';
    close.onclick = function () { overlay.remove(); };
    modal.appendChild(close);

    var h2 = document.createElement('h2');
    h2.textContent = 'Activate Your Tokens';
    h2.style.cssText = 'margin:0 0 8px;font-size:20px;';
    modal.appendChild(h2);

    var sub = document.createElement('p');
    sub.textContent = 'Enter the email you used for your WowStore order.';
    sub.style.cssText = 'margin:0 0 16px;color:#666;font-size:14px;';
    modal.appendChild(sub);

    var input = document.createElement('input');
    input.type = 'email';
    input.placeholder = 'your@email.com';
    input.style.cssText = 'width:100%;padding:12px;border:2px solid #e5e7eb;border-radius:8px;font-size:15px;margin-bottom:12px;box-sizing:border-box;outline:none;';
    input.onfocus = function () { input.style.borderColor = '#ec4899'; };
    input.onblur = function () { input.style.borderColor = '#e5e7eb'; };
    modal.appendChild(input);

    var msg = document.createElement('div');
    msg.style.cssText = 'font-size:13px;margin-bottom:12px;min-height:20px;';
    modal.appendChild(msg);

    var btn = document.createElement('button');
    btn.textContent = 'Activate';
    btn.style.cssText = 'width:100%;padding:12px;background:linear-gradient(135deg,#ec4899,#8b5cf6);color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;';
    btn.onclick = function () {
      var email = input.value.trim();
      if (!email || !email.includes('@')) {
        msg.textContent = 'Please enter a valid email.';
        msg.style.color = '#ef4444';
        return;
      }
      btn.textContent = 'Activating...';
      btn.disabled = true;

      fetch(API_BASE + '/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, browserId: getBrowserId() })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.success) {
            msg.textContent = data.message;
            msg.style.color = '#22c55e';
            btn.textContent = 'Done!';
            trackEvent('credits_activated', { packs: data.activated_count, has_vip: data.has_vip });
            // Refresh quota cache
            invalidateCache();
            fetchQuota(function () {});
            setTimeout(function () { overlay.remove(); }, 2000);
          } else {
            msg.textContent = data.message || 'No credits found.';
            msg.style.color = '#ef4444';
            btn.textContent = 'Activate';
            btn.disabled = false;
          }
        })
        .catch(function () {
          msg.textContent = 'Network error. Please try again.';
          msg.style.color = '#ef4444';
          btn.textContent = 'Activate';
          btn.disabled = false;
        });
    };
    modal.appendChild(btn);

    overlay.appendChild(modal);
    overlay.onclick = function (e) {
      if (e.target === overlay) overlay.remove();
    };
    document.body.appendChild(overlay);
    setTimeout(function () { input.focus(); }, 100);
  }

  // === Analytics Helper ===
  function trackEvent(name, data) {
    if (window.LucyAnalytics && window.LucyAnalytics.track) {
      window.LucyAnalytics.track(name, data);
    }
  }

  // === Pre-fetch quota on load ===
  setTimeout(function () {
    fetchQuota(function () {});
  }, 3000);

  // === Public API ===
  window.LucyQuota = {
    getBrowserId: getBrowserId,
    checkQuota: checkQuota,
    canAfford: canAfford,
    getTokenBalance: getTokenBalance,
    updateCache: updateCache,
    invalidateCache: invalidateCache,
    getRemaining: getRemaining,
    showPaywall: showPaywall,
    showActivation: showActivation,
    fetchQuota: fetchQuota,
    TOKEN_COSTS: TOKEN_COSTS,
    FREE_DAILY_TOKENS: FREE_DAILY_TOKENS,
    PRODUCTS: PRODUCTS
  };

})();
